import { useEffect, useRef } from "react"
import { useDebouncedCallback } from "@tanstack/react-pacer"
import type { TamCalculatorFilters, TamCalculatorFilterOptions } from "~/react/types/tam_calculator"
import { Button } from "~/react/ui/button"
import { AcvFilter } from "~/react/components/tam_calculator_filters/acv_filter"
import { LocationFilter } from "~/react/components/discover_filters/location_filter"
import { IndustryFilter } from "~/react/components/discover_filters/industry_filter"
import { CompanySizeFilter } from "~/react/components/discover_filters/company_size_filter"
import { CompanyTypeFilter } from "~/react/components/discover_filters/company_type_filter"
import { KeywordsFilter } from "~/react/components/discover_filters/keywords_filter"

const t = (key: string) => window.i18n.t(key)

// Helper to ensure value is always an array
function ensureArray(value: unknown): string[] {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (Array.isArray(item)) return String(item[0])
      return String(item)
    })
  }
  if (typeof value === "object") return Object.keys(value)
  return [String(value)]
}

// Helper to capitalize company type labels
function capitalizeCompanyType(type: string): string {
  return type
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

interface TamCalculatorFiltersProps {
  filters: TamCalculatorFilters
  filterOptions: TamCalculatorFilterOptions
  updateFilter: <K extends keyof TamCalculatorFilters>(key: K, value: TamCalculatorFilters[K]) => void
  clearAllFilters: () => void
  hasFilters: boolean
  onFiltersChange: (filters: TamCalculatorFilters) => void
}

export default function TamCalculatorFilters({
  filters,
  filterOptions,
  updateFilter,
  clearAllFilters,
  hasFilters,
  onFiltersChange,
}: TamCalculatorFiltersProps) {
  const isInitialMount = useRef(true)

  const handleAcvChange = (value: number | null) => {
    updateFilter("acv", value || undefined)
  }

  // Auto-apply filters on change (debounced)
  const debouncedOnFiltersChange = useDebouncedCallback(onFiltersChange, { wait: 400 })

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    debouncedOnFiltersChange(filters)
  }, [filters, debouncedOnFiltersChange])

  return (
    <div className="tam-calculator__filters">
      {/* Step 1: Business Assumption */}
      <div className="tam-calculator__calculation-basis">
        <div className="tam-calculator__filters-header mb-3">
          <div className="tam-calculator__step-badge">1</div>
          <strong className="tam-calculator__filters-title">
            {t("js.visitors.tam_calculator.calculation_basis.title")}
          </strong>
        </div>
        <div className="tam-calculator__calculation-basis-content">
          <AcvFilter value={filters.acv || null} onChange={handleAcvChange} />
        </div>
      </div>

      {/* Step 2: Define Your Target */}
      <div className="tam-calculator__filters-section">
        <div className="tam-calculator__filters-header">
          <div className="tam-calculator__filters-header-title">
            <div className="tam-calculator__step-badge">2</div>
            <strong className="tam-calculator__filters-title">{t("js.visitors.tam_calculator.filters.title")}</strong>
          </div>
          {hasFilters && (
            <Button size="xs" onClick={clearAllFilters} className="tam-calculator__filters-clear-all">
              {t("js.visitors.tam_calculator.filters.clear_all")}
            </Button>
          )}
        </div>

        <div className="tam-calculator__filters-list">
          {/* Location Filter */}
          <LocationFilter
            includedContinentValues={ensureArray(filters.location_continent_included)}
            excludedContinentValues={ensureArray(filters.location_continent_excluded)}
            includedBusinessRegionValues={ensureArray(filters.location_business_region_included)}
            excludedBusinessRegionValues={ensureArray(filters.location_business_region_excluded)}
            includedCountryValues={ensureArray(filters.location_country_included)}
            excludedCountryValues={ensureArray(filters.location_country_excluded)}
            includedStateValues={ensureArray(filters.location_state_included)}
            excludedStateValues={ensureArray(filters.location_state_excluded)}
            includedCityValues={ensureArray(filters.location_city_included)}
            excludedCityValues={ensureArray(filters.location_city_excluded)}
            onChangeIncludedContinent={(value) => updateFilter("location_continent_included", value)}
            onChangeExcludedContinent={(value) => updateFilter("location_continent_excluded", value)}
            onChangeIncludedBusinessRegion={(value) => updateFilter("location_business_region_included", value)}
            onChangeExcludedBusinessRegion={(value) => updateFilter("location_business_region_excluded", value)}
            onChangeIncludedCountry={(value) => updateFilter("location_country_included", value)}
            onChangeExcludedCountry={(value) => updateFilter("location_country_excluded", value)}
            onChangeIncludedState={(value) => updateFilter("location_state_included", value)}
            onChangeExcludedState={(value) => updateFilter("location_state_excluded", value)}
            onChangeIncludedCity={(value) => updateFilter("location_city_included", value)}
            onChangeExcludedCity={(value) => updateFilter("location_city_excluded", value)}
            countryNames={filterOptions.country_names}
            stateNames={filterOptions.state_names}
            defaultExpanded={true}
          />

          {/* Industry Filter */}
          <IndustryFilter
            includedValues={ensureArray(filters.industry_included)}
            excludedValues={ensureArray(filters.industry_excluded)}
            industries={filterOptions.industries}
            onChangeIncluded={(value) => updateFilter("industry_included", value)}
            onChangeExcluded={(value) => updateFilter("industry_excluded", value)}
          />

          {/* Company Size Filter */}
          <CompanySizeFilter
            value={ensureArray(filters.company_size)}
            sizes={filterOptions.company_sizes}
            onChange={(value) => updateFilter("company_size", value)}
          />

          {/* Company Type Filter */}
          <CompanyTypeFilter
            includedValues={ensureArray(filters.company_type_included)}
            excludedValues={ensureArray(filters.company_type_excluded)}
            companyTypes={filterOptions.company_types.map((type) => ({
              value: type,
              label: capitalizeCompanyType(type),
            }))}
            onChangeIncluded={(value) => updateFilter("company_type_included", value)}
            onChangeExcluded={(value) => updateFilter("company_type_excluded", value)}
          />

          {/* Keywords Filter */}
          <KeywordsFilter
            includedValues={ensureArray(filters.keywords_included)}
            excludedValues={ensureArray(filters.keywords_excluded)}
            operator="and"
            onChangeIncluded={(value) => updateFilter("keywords_included", value)}
            onChangeExcluded={(value) => updateFilter("keywords_excluded", value)}
            onChangeOperator={() => {}}
          />
        </div>
      </div>
    </div>
  )
}
